import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["form", "contrast"]
  static values = { defaults: Object, messages: Object }

  connect() {
    this.initialValues = this.valuesByName()
    this.updateContrast()
    this.discardHandler = () => this.discard()
    window.addEventListener("theme-settings:discard", this.discardHandler)
  }

  disconnect() {
    window.removeEventListener("theme-settings:discard", this.discardHandler)
  }

  preview(event) {
    const input = event.target
    const match = input.name?.match(/^tokens\[([^\]]+)\]\[([^\]]+)\]$/)
    if (!match) return

    this.applyVariable(`--${match[1]}-${match[2]}`, input.value)
    this.updateContrast()
  }

  applyPreset(event) {
    const values = JSON.parse(event.currentTarget.dataset.values || "{}")
    Object.entries(values).forEach(([name, value]) => this.setNamedValue(name, value))
    this.previewAll()
  }

  reset() {
    Object.entries(this.defaultsValue || {}).forEach(([group, values]) => {
      Object.entries(values || {}).forEach(([key, value]) => this.setNamedValue(`tokens[${group}][${key}]`, value))
    })
    this.previewAll()
  }

  discard() {
    Object.entries(this.initialValues).forEach(([name, value]) => this.setNamedValue(name, value))
    this.previewAll()
  }

  previewAll() {
    Object.entries(this.valuesByName()).forEach(([name, value]) => {
      const match = name.match(/^tokens\[([^\]]+)\]\[([^\]]+)\]$/)
      if (match) this.applyVariable(`--${match[1]}-${match[2]}`, value)
    })
    this.updateContrast()
  }

  valuesByName() {
    const values = {}
    this.formTarget.querySelectorAll("[name^='tokens[']").forEach((input) => { values[input.name] = input.value })
    return values
  }

  setNamedValue(name, value) {
    this.formTarget.querySelectorAll("[name]").forEach((input) => {
      if (input.name !== name) return
      input.value = value
      if (input.type === "color") input.value = this.validHex(value) ? value : "#000000"
    })
  }

  applyVariable(name, value) {
    document.querySelectorAll(".themed-storefront").forEach((element) => element.style.setProperty(name, value))
    const frame = document.querySelector("[data-builder-target~='previewFrame']")
    try { frame?.contentDocument?.querySelector(".themed-storefront")?.style.setProperty(name, value) } catch (_error) { /* same-origin preview only */ }
  }

  updateContrast() {
    if (!this.hasContrastTarget) return
    const values = this.valuesByName()
    const pairs = [
      [this.message("primary_button", "Primary button"), values["tokens[color][primary]"], values["tokens[color][on_primary]"]],
      [this.message("page_text", "Page text"), values["tokens[color][background]"], values["tokens[color][text]"]],
      [this.message("card_text", "Card text"), values["tokens[color][card]"], values["tokens[color][text]"]]
    ]
    const failing = pairs.filter(([, background, foreground]) => this.contrast(background, foreground) < 4.5)
    this.contrastTarget.textContent = failing.length ? `${this.message("low_contrast", "Low contrast")}: ${failing.map(([label]) => label).join(", ")}` : this.message("contrast_good", "Contrast looks good")
    this.contrastTarget.className = failing.length ? "mt-2 text-[10px] text-amber-600" : "mt-2 text-[10px] text-green-600"
  }

  contrast(first, second) {
    if (!this.validHex(first) || !this.validHex(second)) return 0
    const luminance = (hex) => {
      const channels = hex.slice(1).match(/.{2}/g).map((part) => parseInt(part, 16) / 255)
      const adjusted = channels.map((value) => value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4)
      return 0.2126 * adjusted[0] + 0.7152 * adjusted[1] + 0.0722 * adjusted[2]
    }
    const a = luminance(first)
    const b = luminance(second)
    return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05)
  }

  validHex(value) {
    return /^#[0-9a-fA-F]{6}$/.test(value || "")
  }

  message(key, fallback) {
    return this.hasMessagesValue ? (this.messagesValue[key] || fallback) : fallback
  }
}
